import { baseApi } from "@/shared/api/baseApi";
import {
  accountSettingsResponseSchema,
  organizationUpdateResponseSchema,
  passwordChangeResponseSchema,
  preferencesUpdateResponseSchema,
  type CurrencyCode,
  type LocaleCode,
} from "@/features/settings/accountSettingsSchemas";

interface UpdateOrganizationNamePayload {
  expectedRevision: number;
  name: string;
  organizationId: string;
}

interface UpdatePreferencesPayload {
  currencyCode?: CurrencyCode;
  expectedRevision: number;
  locale?: LocaleCode;
}

interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export const accountSettingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    changePassword: builder.mutation<
      ReturnType<typeof passwordChangeResponseSchema.parse>,
      ChangePasswordPayload
    >({
      invalidatesTags: ["MeSettings"],
      query: ({ currentPassword, newPassword }) => ({
        body: {
          current_password: currentPassword,
          new_password: newPassword,
        },
        method: "POST",
        url: "/v1/me/password/change",
      }),
      transformResponse: (response) =>
        passwordChangeResponseSchema.parse(response),
    }),
    deleteAvatar: builder.mutation<void, void>({
      invalidatesTags: ["MeSettings", "MeAvatar"],
      query: () => ({ method: "DELETE", url: "/v1/me/avatar" }),
    }),
    getAccountSettings: builder.query<
      ReturnType<typeof accountSettingsResponseSchema.parse>,
      void
    >({
      providesTags: ["MeSettings", "Organization", "UserPreferences"],
      query: () => "/v1/me/settings",
      transformResponse: (response) =>
        accountSettingsResponseSchema.parse(response),
    }),
    updateOrganizationName: builder.mutation<
      ReturnType<typeof organizationUpdateResponseSchema.parse>,
      UpdateOrganizationNamePayload
    >({
      invalidatesTags: ["MeSettings", "Organization"],
      query: ({ expectedRevision, name, organizationId }) => ({
        body: { expected_revision: expectedRevision, name },
        method: "PATCH",
        url: `/v1/organizations/${encodeURIComponent(organizationId)}`,
      }),
      transformResponse: (response) =>
        organizationUpdateResponseSchema.parse(response),
    }),
    updatePreferences: builder.mutation<
      ReturnType<typeof preferencesUpdateResponseSchema.parse>,
      UpdatePreferencesPayload
    >({
      invalidatesTags: ["MeSettings", "UserPreferences"],
      query: ({ currencyCode, expectedRevision, locale }) => ({
        body: {
          ...(currencyCode ? { currency_code: currencyCode } : {}),
          expected_revision: expectedRevision,
          ...(locale ? { locale } : {}),
        },
        method: "PATCH",
        url: "/v1/me/preferences",
      }),
      transformResponse: (response) =>
        preferencesUpdateResponseSchema.parse(response),
    }),
  }),
});

export const {
  useChangePasswordMutation,
  useDeleteAvatarMutation,
  useGetAccountSettingsQuery,
  useUpdateOrganizationNameMutation,
  useUpdatePreferencesMutation,
} = accountSettingsApi;
